import { createFileRoute } from '@tanstack/react-router';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { costAnalyticsReportEndpoints } from '@/lib/api/endpoints';
import {
  AnalyticsReportShell,
  MetricCard,
  ReportDateRangeControl,
  ReportSection,
  ScorecardTable,
  computeDeltaPct,
  formatCurrency,
  formatInteger,
  formatPercent,
  useReportPeriod,
  type ScorecardColumn,
} from '@/components/organisms/analytics-reports';

interface BvaOverview {
  budgetCost: number;
  actualCost: number;
  costVariance: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  budgetLaborHours: number;
  actualLaborHours: number;
  visitCount: number;
}

interface BvaContractRow {
  jobId: string;
  label: string;
  budgetCost: number;
  actualCost: number;
  costVariance: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  visits: number;
}

interface BvaPropertyRow {
  property: string;
  budgetCost: number;
  actualCost: number;
  costVariance: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  visits: number;
}

interface BvaServiceRow {
  productionKitId: string;
  kitName: string;
  budgetCost: number;
  actualCost: number;
  costVariance: number;
  revenue: number;
  margin: number;
  marginPct: number | null;
  visits: number;
}

interface BudgetVsActualReport {
  period: { from: string | null; to: string | null };
  overview: BvaOverview;
  byContract: BvaContractRow[];
  byProperty: BvaPropertyRow[];
  byService: BvaServiceRow[];
}

export const Route = createFileRoute('/_dashboard/dashboard/jobs/budget-vs-actual')({
  component: BudgetVsActualPage,
});

function BudgetVsActualPage() {
  const period = useReportPeriod('analytics.jobs.budgetVsActual');
  const params = { from: period.from, to: period.to };

  const q = useApiQuery<BudgetVsActualReport>({
    queryKey: ['costing', 'budget-vs-actual', params],
    queryFn: () =>
      apiGet<BudgetVsActualReport>(
        operationsClient,
        costAnalyticsReportEndpoints.budgetVsActual(params),
      ),
    showErrorToast: false,
  });
  const data = q.data;
  const o = data?.overview;

  const sharedColumns = <
    R extends {
      budgetCost: number;
      actualCost: number;
      costVariance: number;
      revenue: number;
      margin: number;
      marginPct: number | null;
      visits: number;
    },
  >(
    firstKey: keyof R & string,
    firstHeader: string,
    firstValue: (r: R) => string,
  ): ScorecardColumn<R>[] => [
    { key: firstKey, header: firstHeader, type: 'text', value: firstValue },
    { key: 'visits', header: 'Visits', type: 'number', value: (r) => r.visits },
    {
      key: 'budgetCost',
      header: 'Budget',
      type: 'currency',
      value: (r) => r.budgetCost,
      total: (rows) => rows.reduce((s, r) => s + r.budgetCost, 0),
    },
    {
      key: 'actualCost',
      header: 'Actual',
      type: 'currency',
      value: (r) => r.actualCost,
      total: (rows) => rows.reduce((s, r) => s + r.actualCost, 0),
    },
    {
      key: 'costVariance',
      header: 'Variance',
      type: 'currency',
      value: (r) => r.costVariance,
      total: (rows) => rows.reduce((s, r) => s + r.costVariance, 0),
    },
    {
      key: 'revenue',
      header: 'Revenue',
      type: 'currency',
      value: (r) => r.revenue,
      total: (rows) => rows.reduce((s, r) => s + r.revenue, 0),
    },
    { key: 'marginPct', header: 'Margin', type: 'percent', value: (r) => r.marginPct ?? 0 },
  ];

  return (
    <AnalyticsReportShell
      title="Budget vs Actual"
      subtitle="Production-kit budget vs realised cost on completed visits"
      backTo="/dashboard/jobs"
      exportDisabled
      actions={<ReportDateRangeControl period={period} />}
    >
      <ReportSection
        title="Overview"
        description="Completed, budgeted visits in the selected range."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <MetricCard
            title="Visits"
            value={o ? formatInteger(o.visitCount) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Budget Cost"
            value={o ? formatCurrency(o.budgetCost) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Actual Cost"
            value={o ? formatCurrency(o.actualCost) : '--'}
            isLoading={q.isLoading}
            deltaPct={computeDeltaPct(o?.actualCost, o?.budgetCost)}
            deltaPositiveIsGood={false}
          />
          <MetricCard
            title="Cost Variance"
            value={o ? formatCurrency(o.costVariance) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Revenue"
            value={o ? formatCurrency(o.revenue) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Margin"
            value={o ? formatCurrency(o.margin) : '--'}
            isLoading={q.isLoading}
          />
          <MetricCard
            title="Margin %"
            value={o?.marginPct != null ? formatPercent(o.marginPct) : '--'}
            isLoading={q.isLoading}
          />
        </div>
      </ReportSection>

      <ReportSection title="By contract">
        <ScorecardTable<BvaContractRow>
          rows={data?.byContract ?? []}
          isLoading={q.isLoading}
          emptyMessage="No completed budgeted visits in this range"
          initialSortKey="costVariance"
          columns={sharedColumns<BvaContractRow>('label', 'Contract', (r) => r.label)}
        />
      </ReportSection>

      <ReportSection title="By property">
        <ScorecardTable<BvaPropertyRow>
          rows={data?.byProperty ?? []}
          isLoading={q.isLoading}
          emptyMessage="No completed budgeted visits in this range"
          initialSortKey="costVariance"
          columns={sharedColumns<BvaPropertyRow>('property', 'Property', (r) => r.property)}
        />
      </ReportSection>

      <ReportSection
        title="Profitable services"
        description="Per production-kit roll-up across the range."
      >
        <ScorecardTable<BvaServiceRow>
          rows={data?.byService ?? []}
          isLoading={q.isLoading}
          emptyMessage="No production-kit attributed visits in this range"
          initialSortKey="margin"
          columns={sharedColumns<BvaServiceRow>('kitName', 'Service kit', (r) => r.kitName)}
        />
      </ReportSection>
    </AnalyticsReportShell>
  );
}
